/**
 * A customer's detail page (F5).
 *
 * Read-only. Editing a customer needs a form with owner pickers backed by a
 * user directory, and that directory is task 7.12 — building a half-form now
 * with free-text owner ids would produce a control that saves broken data.
 * Reading is what unblocks the rest of Phase 7: pilots, assets and contacts all
 * link here.
 *
 * The ownership card comes first in the sidebar, because PRD §5.5 makes
 * "whose account is this?" the question this record exists to answer.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 7.3, 7.12, PRD §5.5
 */
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { DefinitionList } from '../../components/domain/DefinitionList';
import { RelationshipStageBadge } from '../../components/domain/RelationshipStageBadge';
import { Badge } from '../../components/ui/Badge';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Grid, Stack } from '../../components/ui/Grid';
import { Kicker } from '../../components/ui/Kicker';
import { Note } from '../../components/ui/Note';
import { PageHead } from '../../components/ui/PageHead';
import { Stat } from '../../components/ui/Stat';
import { formatDate, formatNumber } from '../../lib/format';
import { ROUTES } from '../../lib/routes';

import { useCustomer } from './api/customers.queries';

export function CustomerDetailPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();

  const { data, isPending, isError } = useCustomer(id);

  if (isPending) {
    return (
      <p role="status" className="text-body">
        {t('state.loading')}
      </p>
    );
  }

  if (isError) {
    return (
      <div>
        <p role="alert" className="text-sem-bad mb-s2">
          {t('customer.notFound')}
        </p>
        <Link to={ROUTES.customers} className="text-blue text-sm underline">
          {t('customer.backToList')}
        </Link>
      </div>
    );
  }

  const notSet = <span className="text-gray-400">{t('startup.notSet')}</span>;

  /** An owner whose user record is gone still has to read as a real gap. */
  const owner = (name: string | null): React.ReactNode =>
    name ?? <Badge tone="warn">{t('customer.columns.unassigned')}</Badge>;

  return (
    <>
      <nav aria-label={t('startup.breadcrumb')} className="text-body mb-s2 text-xs">
        {/* Persistently underlined, not only on hover: inside a text block a
            link distinguished by colour alone fails WCAG 1.4.1 (PRD §9.7). */}
        <Link to={ROUTES.customers} className="text-blue underline">
          {t('customer.title')}
        </Link>
        <span className="text-gray-200 mx-1.5">/</span>
        <span>{data.name}</span>
      </nav>

      <PageHead
        title={data.name}
        actions={
          <>
            <RelationshipStageBadge stage={data.relationshipStage} />
            {data.isTouchOverdue ? (
              <Badge tone="bad">{t('customer.overdue')}</Badge>
            ) : null}
          </>
        }
      />

      <Grid cols="detail">
        <Stack>
          {/* The three counts that say whether this account is active rather
              than merely recorded. Green because the figure IS the data
              (§4.2). */}
          <Grid cols={3}>
            <Card>
              <Stat
                label={t('customer.columns.pilots')}
                value={formatNumber(data.pilotCount)}
              />
            </Card>
            <Card>
              <Stat
                label={t('customer.columns.assets')}
                value={formatNumber(data.assetCount)}
              />
            </Card>
            <Card>
              <Stat
                label={t('customer.columns.contacts')}
                value={formatNumber(data.contactCount)}
              />
            </Card>
          </Grid>

          <Card>
            <CardHead>
              <Kicker as="h2">{t('customer.sections.profile')}</Kicker>
            </CardHead>
            <CardBody>
              <DefinitionList
                items={[
                  {
                    label: t('customer.fields.country'),
                    value: data.country ?? notSet,
                  },
                  {
                    label: t('customer.fields.technologies'),
                    value:
                      data.technologyInterestIds.length === 0
                        ? notSet
                        : formatNumber(data.technologyInterestIds.length),
                  },
                  {
                    label: t('customer.fields.drivers'),
                    value:
                      data.decarbonisationDriverIds.length === 0
                        ? notSet
                        : formatNumber(data.decarbonisationDriverIds.length),
                  },
                ]}
              />
            </CardBody>
          </Card>

          {data.accountNotes === null ? null : (
            <Card>
              <CardHead>
                <Kicker as="h2">{t('customer.sections.notes')}</Kicker>
              </CardHead>
              <CardBody>
                <p className="text-body text-[13px] whitespace-pre-line">
                  {data.accountNotes}
                </p>
              </CardBody>
            </Card>
          )}
        </Stack>

        <Stack>
          {/* First in the sidebar: PRD §5.5 makes this the question the record
              exists to answer. */}
          <Card>
            <CardHead>
              <Kicker as="h2">{t('customer.sections.ownership')}</Kicker>
            </CardHead>
            <CardBody>
              <DefinitionList
                items={[
                  {
                    label: t('customer.fields.innovationOwner'),
                    value: owner(data.innovationOwnerName),
                  },
                  {
                    label: t('customer.fields.commercialOwner'),
                    value: owner(data.commercialOwnerName),
                  },
                ]}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHead>
              <Kicker as="h2">{t('customer.sections.contact')}</Kicker>
            </CardHead>
            <CardBody>
              <DefinitionList
                items={[
                  {
                    label: t('customer.fields.lastTouch'),
                    value:
                      data.lastTouchDate === null
                        ? notSet
                        : formatDate(data.lastTouchDate),
                  },
                  {
                    label: t('customer.fields.nextTouch'),
                    value:
                      data.nextTouchDate === null
                        ? notSet
                        : formatDate(data.nextTouchDate),
                  },
                ]}
              />
            </CardBody>
          </Card>

          {/* Says why there is no Edit button, rather than leaving a reader to
              conclude the page is broken. */}
          <Note>{t('customer.readOnlyNote')}</Note>
        </Stack>
      </Grid>
    </>
  );
}
